/**
 * Admin Guard
 *
 * Navigation guard для защиты админ-маршрутов.
 * Проверяет, что пользователь авторизован и является системным администратором.
 */

import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { SYSTEM_ADMIN_EMAIL } from '@/composables/useAdminAccess'
import { useAuthStore } from '@/stores/auth'

/**
 * Ждём инициализации auth (максимум 2 секунды)
 */
async function waitForAuth(authStore: ReturnType<typeof useAuthStore>): Promise<void> {
  // Если уже инициализирован — выходим сразу
  if (authStore.isAuthenticated) return

  // Пробуем восстановить из localStorage напрямую
  const storedUser = localStorage.getItem('tlh:auth:user')
  if (storedUser) {
    // Auth store ещё не инициализирован, но данные есть — подождём
    const maxWait = 2000
    const interval = 50
    let waited = 0

    while (!authStore.isAuthenticated && waited < maxWait) {
      await new Promise((r) => setTimeout(r, interval))
      waited += interval
    }
  }
}

/**
 * Guard для защиты админ-маршрутов
 * Редиректит на главную, если пользователь не является system admin
 *
 * В режиме DEV (Vite dev server) можно обойти проверку авторизации,
 * добавив ?devAdmin=true в URL
 */
export async function adminGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> {
  // DEV_MODE: разрешить доступ с ?devAdmin=true в dev-режиме
  if (import.meta.env.DEV && to.query.devAdmin === 'true') {
    console.log('[AdminGuard] DEV_MODE: bypassing auth check')
    return next()
  }

  const authStore = useAuthStore()

  // Ждём инициализации auth
  await waitForAuth(authStore)

  // Проверяем авторизацию
  if (!authStore.isAuthenticated) {
    console.warn('[AdminGuard] User not authenticated, redirecting to home')
    return next({ name: 'Home' })
  }

  // Проверяем, что это системный администратор
  if (authStore.user?.email !== SYSTEM_ADMIN_EMAIL) {
    console.warn('[AdminGuard] User is not system admin, redirecting to home')
    return next({ name: 'Home' })
  }

  // Доступ разрешён
  next()
}
